import React, { useEffect, useState } from "react";
import { api, fmtShipDate } from "../lib/api";
import { ChevronLeft, ChevronRight, FileText, FileBadge, MapPin, Anchor, Calendar, Ruler, Users as UsersIcon, Wind, Image as ImageIcon } from "lucide-react";

const PLACEHOLDER = "https://images.pexels.com/photos/4170111/pexels-photo-4170111.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";

export default function Ship() {
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);

  const load = async () => {
    try {
      const { data: d } = await api.get("/ship");
      setData(d && typeof d === "object" ? d : null);
      setIdx(0);
    } catch (e) { console.error("Ship load:", e); setData(null); }
  };
  useEffect(() => { load(); }, []);

  const backend = process.env.REACT_APP_BACKEND_URL;
  const imgs = data?.images || [];
  const current = imgs[idx];
  const prev = () => setIdx((i) => (imgs.length ? (i - 1 + imgs.length) % imgs.length : 0));
  const next = () => setIdx((i) => (imgs.length ? (i + 1) % imgs.length : 0));

  if (!data) return <div className="tk-card p-6 text-sm" style={{ color: "hsl(var(--text-2))" }}>Yükleniyor...</div>;
  const s = data.info;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-display font-semibold flex items-baseline flex-wrap gap-x-2">
          <span>{s.name}</span>
          <span className="font-normal text-sm sm:text-base" style={{ color: "hsl(var(--text-2))" }}>
            · {s.category} · {s.route}
          </span>
        </h1>
      </div>

      {/* Slider */}
      <div className="tk-card p-0 overflow-hidden relative" data-testid="ship-slider">
        <div className="relative w-full" style={{ aspectRatio: "16/9", backgroundColor: "hsl(var(--surface-2))" }}>
          <img
            src={current ? `${backend}${current.url}` : PLACEHOLDER}
            alt={`Gemi ${idx + 1}`}
            className="w-full h-full object-cover"
          />
          {imgs.length > 1 && (
            <>
              <button data-testid="slider-prev-btn" onClick={prev} className="absolute top-1/2 left-3 -translate-y-1/2 w-10 h-10 rounded-full bg-white/85 backdrop-blur flex items-center justify-center shadow"><ChevronLeft className="w-5 h-5" style={{ color: "hsl(var(--text-1))" }} /></button>
              <button data-testid="slider-next-btn" onClick={next} className="absolute top-1/2 right-3 -translate-y-1/2 w-10 h-10 rounded-full bg-white/85 backdrop-blur flex items-center justify-center shadow"><ChevronRight className="w-5 h-5" style={{ color: "hsl(var(--text-1))" }} /></button>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                {imgs.map((_, i) => (
                  <div key={i} className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: i === idx ? "hsl(var(--primary))" : "rgba(255,255,255,0.6)" }} />
                ))}
              </div>
            </>
          )}
          {imgs.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
              <div className="text-white text-sm flex items-center gap-2 backdrop-blur rounded-full px-3 py-1.5 bg-white/10">
                <ImageIcon className="w-4 h-4" /> Henüz görsel yüklenmedi
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Spec grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Spec icon={Anchor} label="Tekne" value={s.name} />
        <Spec icon={Ruler} label="Boy × En" value={`${s.length_m} × ${s.width_m} m`} />
        <Spec icon={UsersIcon} label="Kabin / Kapasite" value={`${s.cabin_count} kabin · ${s.max_guests} kişi`} />
        <Spec icon={Wind} label="Klima" value={s.ac_usage} />
        <Spec icon={MapPin} label="Liman" value={s.port} />
        <Spec icon={Calendar} label="Başlangıç" value={fmtShipDate(s.start)} />
        <Spec icon={Calendar} label="Bitiş" value={fmtShipDate(s.end)} />
        <Spec icon={Anchor} label="Tekne Kodu" value={s.code} />
      </div>

      <div className="tk-card p-5 space-y-1">
        <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "hsl(var(--text-2))" }}>Rota</div>
        <div className="text-base font-display">{s.route}</div>
      </div>

      {/* Parties */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="tk-card p-5">
          <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "hsl(var(--text-2))" }}>Kiralayan</div>
          <div className="mt-1 font-medium">{s.charterer}</div>
        </div>
        <div className="tk-card p-5">
          <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "hsl(var(--text-2))" }}>Kiracı</div>
          <div className="mt-1 font-medium">{s.lessee}</div>
        </div>
      </div>

      {/* Included / Excluded */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="tk-card p-5">
          <div className="font-display font-semibold mb-2">Fiyata Dahil</div>
          <ul className="space-y-1 text-sm" style={{ color: "hsl(var(--text-2))" }}>
            {s.included.map((t, i) => <li key={i} className="flex items-start gap-2"><span style={{ color: "hsl(var(--success))" }}>✓</span><span>{t}</span></li>)}
          </ul>
        </div>
        <div className="tk-card p-5">
          <div className="font-display font-semibold mb-2">Dahil Değil</div>
          <ul className="space-y-1 text-sm" style={{ color: "hsl(var(--text-2))" }}>
            {s.excluded.map((t, i) => <li key={i} className="flex items-start gap-2"><span style={{ color: "hsl(var(--error))" }}>✕</span><span>{t}</span></li>)}
          </ul>
        </div>
      </div>

      {/* Documents */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <a data-testid="ship-contract-btn" href={`${backend}${data.contract_url}`} target="_blank" rel="noopener noreferrer" className="tk-card p-4 flex items-center gap-3 hover:bg-[hsl(var(--surface-2))] transition">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: "hsl(var(--surface-2))" }}>
            <FileText className="w-5 h-5" style={{ color: "hsl(var(--primary))" }} />
          </div>
          <div className="flex-1">
            <div className="font-display font-semibold">Kira Sözleşmesi</div>
            <div className="text-sm" style={{ color: "hsl(var(--text-2))" }}>Juva — Mavi Yolculuk · PDF</div>
          </div>
          <div style={{ color: "hsl(var(--text-2))" }}>→</div>
        </a>

        <a data-testid="ship-factsheet-btn" href={`${backend}${data.factsheet_url || "/api/ship/factsheet"}`} target="_blank" rel="noopener noreferrer" className="tk-card p-4 flex items-center gap-3 hover:bg-[hsl(var(--surface-2))] transition">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: "hsl(var(--surface-2))" }}>
            <FileBadge className="w-5 h-5" style={{ color: "hsl(var(--primary))" }} />
          </div>
          <div className="flex-1">
            <div className="font-display font-semibold">Fact Sheet</div>
            <div className="text-sm" style={{ color: "hsl(var(--text-2))" }}>F6022E · teknik bilgi · PDF</div>
          </div>
          <div style={{ color: "hsl(var(--text-2))" }}>→</div>
        </a>
      </div>
    </div>
  );
}

function Spec({ icon: Icon, label, value }) {
  return (
    <div className="tk-card p-4">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4" style={{ color: "hsl(var(--text-2))" }} />
        <span className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "hsl(var(--text-2))" }}>{label}</span>
      </div>
      <div className="text-base font-display font-semibold">{value}</div>
    </div>
  );
}
